"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useTranslations } from "next-intl";

import { BASE_SITE_CURRENCY, createSiteMoney, type SiteCurrency, type SiteMoney } from "@/lib/site/currency";

const SiteCurrencyContext = createContext<SiteCurrency>(BASE_SITE_CURRENCY);

export function SiteCurrencyProvider({ currency, children }: { currency: SiteCurrency; children: ReactNode }) {
  return <SiteCurrencyContext.Provider value={currency}>{children}</SiteCurrencyContext.Provider>;
}

export function useSiteMoney(): SiteMoney {
  const currency = useContext(SiteCurrencyContext);

  return useMemo(() => createSiteMoney(currency), [currency]);
}

export function useChargeLabel(): (cny: number) => string {
  const money = useSiteMoney();
  const t = useTranslations("site.common");

  return (cny) =>
    money.converted ? t("priceWithApprox", { base: money.base(cny), approx: money.approx(cny) }) : money.base(cny);
}

export function ApproxPrice({ value, className }: { value: number; className?: string }) {
  const money = useSiteMoney();
  const t = useTranslations("site.common");

  return money.converted ? <span className={className}>{t("approxPrice", { amount: money.approx(value) })}</span> : null;
}

export function SitePrice({ value, split }: { value: number; split?: boolean }) {
  const money = useSiteMoney();

  if (!split) {
    return <>{money.price(value)}</>;
  }

  return <SplitAmount money={money} amount={money.priceValue(value)} />;
}

export function SplitAmount({ money, amount }: { money: SiteMoney; amount: number }) {
  const { symbol, number, symbolAfter } = money.parts(amount);

  return symbolAfter ? (
    <>
      {number}
      <i>{symbol}</i>
    </>
  ) : (
    <>
      <i>{symbol}</i>
      {number}
    </>
  );
}

export function PriceAmount({ amount }: { amount: number }) {
  const { symbol, number, symbolAfter } = useSiteMoney().parts(amount);

  return symbolAfter ? (
    <>
      <span>{number}</span>
      <i>{symbol}</i>
    </>
  ) : (
    <>
      <i>{symbol}</i>
      <span>{number}</span>
    </>
  );
}
